import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { ClearFilters } from "@/app/ClearFilters";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { accounts, alertThresholds, channels, customers, type AlertRule, type AlertThreshold, type AlertThresholdInput } from "@/app/master";
import { accountOptions, idOptions, loadingOf, useList } from "@/app/queries";
import { Alert, Button, EmptyState, FILTER_W, Input as TextInput, Money, PageHeader, Pill, Select, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { AlertThresholdForm } from "./AlertThresholdForm";
import { ALERT_RULES, RULE_ORDER, SCOPE_OPTIONS, alertThresholdDraft, alertThresholdInput, conditionText, scopeKind, scopeText, searchText, specificity, type ScopeKind } from "./alertThresholdDraft";

const FILTERS = [{ value: "", label: "全部规则" }, ...RULE_ORDER.map((r) => ({ value: r, label: ALERT_RULES[r].label }))];

/** 告警阈值（决策 D190、D191）：六条告警的阈值按全局默认与按通道、账号、运营商的覆盖配置，最具体的一行生效；metrics 最多约一分半内读到并导出，
 * Prometheus 按它判断，不再改规则文件。新建与编辑在抽屉里，保存不发布配置版本。 */
export function AlertThresholds() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [rule, setRule] = useState<AlertRule | "">("");
  const [scope, setScope] = useState<ScopeKind | "">("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: AlertThreshold | null } | null>(null);
  const list = useList("alert-thresholds", alertThresholds);
  const accountList = useList("accounts", accounts).data;
  const customerList = useList("customers", customers).data;
  const channelOptions = idOptions(useList("channels", channels).data);
  const accountOpts = accountOptions(accountList, customerList);
  const optionLabel = (opts: { value: string; label: string }[], id: number) => opts.find((o) => o.value === String(id))?.label ?? `#${id}`;
  const ownerName = (t: AlertThreshold) => (t.channel_id !== null ? optionLabel(channelOptions, t.channel_id) : optionLabel(accountOpts, t.account_id ?? 0));
  const currencyOf = (accountID: number | string) => {
    const a = accountList?.find((x) => String(x.id) === String(accountID));
    return customerList?.find((c) => c.id === a?.customer_id)?.currency;
  };
  const keyword = q.trim().toLowerCase();
  const shown = (list.data ?? [])
    .filter((t) => (!rule || t.rule === rule) && (!scope || scopeKind(t) === scope))
    .filter((t) => !keyword || searchText(t, ownerName).includes(keyword))
    .sort((a, b) => RULE_ORDER.indexOf(a.rule) - RULE_ORDER.indexOf(b.rule) || specificity(a) - specificity(b) || a.id - b.id);
  const title = (t: AlertThreshold) => `${ALERT_RULES[t.rule].label} · ${scopeText(t, ownerName)}`;

  return (
    <>
      <PageHeader title="告警阈值" description="每条规则一行全局默认，可按通道、账号、MCC 或运营商覆盖，最具体的一行生效；保存后约一分半生效，临时屏蔽仍在 Alertmanager 静默。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建覆盖</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar actions={(rule || scope || q) && <ClearFilters onClear={() => { setRule(""); setScope(""); setQ(""); }} />} right={<ToolbarCount>{shown.length} 条</ToolbarCount>}>
        <Select size="sm" width={FILTER_W.select} options={FILTERS} value={rule} onChange={(v) => setRule(v as AlertRule | "")} ariaLabel="按规则筛选" />
        <Select size="sm" width={FILTER_W.select} options={SCOPE_OPTIONS} value={scope} onChange={(v) => setScope(v as ScopeKind | "")} ariaLabel="按对象类型筛选" />
        <TextInput size="sm" className="ax-search" placeholder="搜索通道、账号、客户、ID 或 MCC" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {shown.length ? (
          <Table>
            <thead><tr><Th w={160}>规则</Th><Th w="wide">作用对象</Th><Th w="wide">告警条件</Th><Th w={240}>备注</Th><Th /></tr></thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td>{ALERT_RULES[t.rule].label}</td>
                  <td>{specificity(t) === 0 ? <Pill>全局默认</Pill> : scopeText(t, ownerName)}</td>
                  <td><Condition t={t} currency={t.account_id !== null ? currencyOf(t.account_id) : undefined} /></td>
                  <td className="ax-secondary">{t.note}</td>
                  <td><Button size="sm" variant="ghost" onClick={() => setEditing({ row: t })}>{canWrite ? "编辑" : "查看"}</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !list.isPending && <EmptyState title={list.data?.length ? "没有符合条件的告警阈值" : "没有告警阈值"}>{list.data?.length ? "" : "迁移会写入六条全局默认；被删掉时告警按内置默认判断。"}</EmptyState>
        )}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑告警阈值 ${title(editing.row)}` : "新建告警阈值覆盖"}
          initial={alertThresholdDraft(editing.row ?? undefined)} convert={alertThresholdInput}
          save={(body: AlertThresholdInput) => (editing.row ? alertThresholds.update(editing.row.id, body) : alertThresholds.create(body))}
          invalidate={["alert-thresholds"]} message={editing.row ? "已更新告警阈值，最多约一分半后生效" : "已新建告警阈值，最多约一分半后生效"}
          deletion={editing.row ? deleteTarget("告警阈值", title(editing.row), alertThresholds, editing.row.id, ["alert-thresholds"]) : undefined} onClose={() => setEditing(null)}>
          {(f) => <AlertThresholdForm draft={f.draft} errors={f.errors} set={f.set} isNew={!editing.row} channels={channelOptions} accounts={accountOpts} currencyOf={currencyOf} />}
        </EntityDrawer>
      )}
    </>
  );
}

/** 告警条件：不告警用灰色徽标；余额告警线带账号币种的金额，全局行没有币种，只能是 0（docs/03 4.7）。 */
function Condition({ t, currency }: { t: AlertThreshold; currency?: string }) {
  if (t.mode === "off") return <Pill>不告警</Pill>;
  if (t.rule !== "balance_low") return <>{conditionText(t)}</>;
  return t.account_id === null ? <>余额低于 0（开始用信用额度时）</> : <>余额低于 <Money micro={t.value} currency={currency} /></>;
}
